/* «Фотосессия из селфи», «Семейный портрет», шаблон-фотосессия (APP-SPEC-5 §4; docs/03-api.md §6.6, §9.2 `shoot`).
   Данные экрана — GET /app/studio/screens/shoot (образы, фотосессии, действия, чипы, форматы, цена); ключи заказа — поля формы. */
import type { StudioForm } from '../studio/studio-api';
import { record, rows, text } from '../studio/studio-content';
import { fieldOf, firstField, hasField, listOf, requiredGap, valueOf, type Inputs } from './kit/form-model';

export interface Shot {
  name: string;
  image: string | null;
}
export interface Look {
  id: string;
  name: string;
  description: string;
  themes: string[];
  popularity: number;
  isNew: boolean;
  hit: boolean;
  cover: string | null;
  shots: Shot[];
}
export interface ShootKind {
  id: string;
  /** образы выбирает человек (neuro); иначе всё входит (family, шаблон) */
  pick: boolean;
  looks: string[];
  maxPeople: number;
  actions: string[];
}
export interface ShootScreen {
  looks: Look[];
  kind: ShootKind;
  formats: Array<{ id: string; name: string; sub: string }>;
  wishes: string[];
  themes: string[];
  shotsPerLook: number;
}
const strings = (value: unknown) => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string' && !!item) : []);
const image = (value: Record<string, unknown>) => text(value.image_url) || text(value.url) || text(value.cover_url) || null;

export function parseLooks(value: unknown): Look[] {
  return rows(value).flatMap(item => {
    const id = typeof item.id === 'number' ? String(item.id) : text(item.id);
    if (!id || !text(item.name)) return [];
    const shots = rows(item.shots).map(shot => ({ name: text(shot.name) || text(shot.title), image: image(shot) }));
    return [
      {
        id,
        name: text(item.name),
        description: text(item.description) || text(item.sub),
        themes: strings(item.themes),
        popularity: Number(item.popularity) || 0,
        isNew: item.new === true || item.is_new === true,
        hit: item.hit === true,
        cover: image(item) ?? shots.find(shot => shot.image)?.image ?? null,
        shots,
      },
    ];
  });
}

/** Какая это фотосессия: поле формы, ограничения шаблона, вход экрана; без данных — общая с выбором образов. */
export function shootScreen(data: Record<string, unknown>, form: StudioForm | null, inputs: Inputs, entry: Record<string, unknown>): ShootScreen {
  const constraints = record(record(form?.constraints).shoot);
  const field = fieldOf(form, 'shoot_id');
  /* Поле стенда — JSON-перечисление: во входах лежит '"neuro"'. Читать его сырым нельзя — ShootOrderForm
     записывает вид обратно через encode, и кавычки нарастали бы бесконечно (зависание вкладки). */
  const id =
    valueOf(form, inputs, 'shoot_id') ||
    text(constraints.shoot_id) ||
    (field?.values.length === 1 ? field.values[0] : '') ||
    text(entry.shoot) ||
    'neuro';
  const looks = parseLooks(constraints.looks ?? data.looks);
  const kinds = rows(data.shoots);
  const raw = record(kinds.find(item => text(item.id) === id));
  const fixed = strings(raw.looks ?? raw.look_ids);
  const pick = id === 'app' ? false : typeof raw.pick === 'boolean' ? raw.pick : !fixed.length;
  return {
    looks,
    kind: {
      id,
      pick,
      looks: id === 'app' ? looks.slice(0, 1).map(look => look.id) : fixed,
      maxPeople: Number(constraints.max_people ?? raw.max_people) || (id === 'app' ? 2 : 4),
      actions: strings(raw.actions).length ? strings(raw.actions) : strings(data.actions),
    },
    formats: rows(data.formats).flatMap(item => (text(item.id) ? [{ id: text(item.id), name: text(item.name), sub: text(item.sub) || text(item.ratio) }] : [])),
    wishes: strings(data.wishes),
    themes: strings(data.themes),
    shotsPerLook: Number(data.shots_per_look) || 3,
  };
}

/** Ключи заказа фотосессии: документированные (§9.2) или их синонимы стенда. */
export const shootKeys = (form: StudioForm | null) => ({
  looks: firstField(form, ['look_ids', 'looks', 'look'])?.key,
  ratio: firstField(form, ['ratio', 'format'])?.key,
  wishes: firstField(form, ['wishes', 'text', 'description'])?.key,
  me: hasField(form, 'me') ? 'me' : undefined,
  persons: hasField(form, 'persons') ? 'persons' : undefined,
  chars: hasField(form, 'chars') ? 'chars' : undefined,
  shoot: hasField(form, 'shoot') ? 'shoot' : undefined,
  shootId: hasField(form, 'shoot_id') ? 'shoot_id' : undefined,
});

export function chosenLooks(screen: ShootScreen, inputs: Inputs, key: string | undefined): string[] {
  if (!screen.kind.pick) return screen.kind.looks.length ? screen.kind.looks : screen.looks.map(look => look.id);
  return key ? listOf(inputs[key]).filter(id => id !== 'ref') : [];
}
export function frames(screen: ShootScreen, looks: string[], reference: boolean) {
  const count = looks.reduce((sum, id) => sum + (screen.looks.find(look => look.id === id)?.shots.length || screen.shotsPerLook), 0);
  return count + (reference ? screen.shotsPerLook : 0);
}

export function shootMissing({
  form,
  inputs,
  screen,
  looks,
  reference,
  meReady,
  rights,
  photoFields,
  wishLimit,
  priceConfirmed,
}: {
  form: StudioForm;
  inputs: Inputs;
  screen: ShootScreen;
  looks: string[];
  reference: boolean;
  meReady: boolean;
  rights: boolean;
  photoFields: string[];
  wishLimit: number;
  priceConfirmed: boolean;
}): string | null {
  const keys = shootKeys(form);
  const uploadsRequired = form.fields.some(field => field.upload && field.required);
  if (keys.me && !meReady) return 'Загрузите свои фото';
  if (photoFields.length && uploadsRequired && !rights) return 'Подтвердите согласие людей на фото';
  if (photoFields.some(key => fieldOf(form, key)?.required && !inputs[key])) return 'Загрузите фото';
  if (screen.kind.pick && keys.looks && !looks.length && !reference) return 'Выберите образ';
  const wishes = keys.wishes ? inputs[keys.wishes] ?? '' : '';
  if (Array.from(wishes).length > wishLimit) return 'Пожелания длиннее допустимого';
  if (keys.wishes && fieldOf(form, keys.wishes)?.required && !wishes.trim()) return 'Добавьте пожелания';
  if (!priceConfirmed) return 'Цена такого состава пока не подтверждена: обновите форму';
  return requiredGap(form, inputs, [...photoFields, ...Object.values(keys).filter((key): key is string => !!key)]);
}
